import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import api from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { History, Wallet } from "lucide-react";

const dt = (s) => {
  if (!s) return "-";
  try {
    const d = new Date(s);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return formatDate(s); }
};

export default function CashbackDisbursements() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/cashback/disbursements").then((r) => setData(r.data)); }, []);
  if (!data) return <PageSkeleton stats={1} rows={5} />;

  return (
    <div className="space-y-4" data-testid="disbursement-page">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Riwayat Pencairan Cashback</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Log audit seluruh pencairan cashback — siapa, kapan, dan ke akun mana.</p>
      </div>

      <Card data-testid="disbursement-total-card" className="p-4 max-w-sm">
        <div className="flex items-center gap-2.5">
          <div className="grid place-items-center h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-950 shrink-0"><Wallet className="h-4 w-4 text-emerald-600" /></div>
          <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Dicairkan ({data.count}x)</p><p className="font-heading text-lg font-extrabold truncate">{formatIDR(data.total)}</p></div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Waktu Pencairan</TableHead><TableHead>Pelanggan</TableHead>
                <TableHead className="text-right">Nominal</TableHead><TableHead>Akun / Sumber Dana</TableHead>
                <TableHead>Dicairkan Oleh</TableHead><TableHead>Disetujui Oleh</TableHead><TableHead>Catatan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-40" />Belum ada riwayat pencairan cashback.
                </TableCell></TableRow>
              )}
              {data.items.map((d) => (
                <TableRow key={d.id} data-testid={`disbursement-row-${d.id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{dt(d.at)}</TableCell>
                  <TableCell className="font-medium">{d.customer_name}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600 whitespace-nowrap">{formatIDR(d.amount)}</TableCell>
                  <TableCell className="text-sm">{d.account}</TableCell>
                  <TableCell className="text-sm">{d.by} <span className="text-muted-foreground text-xs">({d.by_role})</span></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.approved_by || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] whitespace-normal break-words">{d.note || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="lg:hidden divide-y divide-border">
          {data.items.length === 0 && (
            <p className="text-center text-muted-foreground text-xs py-8">Belum ada riwayat pencairan cashback.</p>
          )}
          {data.items.map((d) => (
            <div key={d.id} data-testid={`disbursement-row-${d.id}`} className="p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{d.customer_name}</p>
                  <p className="text-[11px] text-muted-foreground">{dt(d.at)}</p>
                </div>
                <p className="text-sm font-bold text-emerald-600 whitespace-nowrap">{formatIDR(d.amount)}</p>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                <div className="flex justify-between"><span className="text-muted-foreground">Akun</span><span>{d.account}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Dicairkan Oleh</span><span>{d.by}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Disetujui Oleh</span><span>{d.approved_by || "-"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Catatan</span><span className="text-right break-words min-w-0">{d.note || "-"}</span></div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
